<script lang="ts">
export default {
  name: "AppNavigationFleetNav",
};
</script>

<script lang="ts" setup>
import NavItem from "@/shared/components/AppNavigation/NavItem/index.vue";
import { useI18n } from "@/shared/composables/useI18n";
import { useComlink } from "@/shared/composables/useComlink";
import {
  useFleet as useFleetQuery,
  usePublicFleet as usePublicFleetQuery,
  useFleetMembership as useFleetMembershipQuery,
  FeatureFlagName,
} from "@/services/fyApi";
import { useSessionStore } from "@/frontend/stores/session";
import { useFeatures } from "@/frontend/composables/useFeatures";

const { t } = useI18n();
const { isFeatureEnabled, isFleetFeatureEnabled } = useFeatures();

const route = useRoute();

const sessionStore = useSessionStore();

const fleetSlug = computed(() => {
  return route.params.slug as string;
});

const hasSlug = computed(() => route.params.slug !== undefined);

const { data: fleet, refetch } = useFleetQuery(fleetSlug, {
  query: {
    retry: false,
    enabled: computed(() => hasSlug.value && sessionStore.isAuthenticated),
  },
});

const { data: publicFleet } = usePublicFleetQuery(fleetSlug, {
  query: {
    retry: false,
    enabled: computed(
      () => hasSlug.value && (!sessionStore.isAuthenticated || !fleet.value),
    ),
  },
});

const { data: membership } = useFleetMembershipQuery(fleetSlug, {
  query: {
    retry: false,
    enabled: computed(() => hasSlug.value && sessionStore.isAuthenticated),
  },
});

const currentFleet = computed(() => {
  return fleet.value || publicFleet.value;
});

const shipsNavActive = computed(() => {
  return ["fleet-ships", "fleet-fleetchart"].includes(String(route.name));
});

const hasLogisticsAccess = computed(
  () => membership.value?.capabilities?.readInventories ?? false,
);

const hasMissionsAccess = computed(() => {
  const access = membership.value?.fleetRole?.resourceAccess;
  if (!access) return false;
  return access.some((a: string) =>
    ["fleet:manage", "fleet:missions:manage", "fleet:missions:read"].includes(
      a,
    ),
  );
});

const hasEventsAccess = computed(() => {
  const access = membership.value?.fleetRole?.resourceAccess;
  if (!access) return false;
  return access.some((a: string) =>
    ["fleet:manage", "fleet:events:manage", "fleet:events:read"].includes(a),
  );
});

const eventsNavActive = computed(() => {
  const name = String(route.name ?? "");
  if (name.startsWith("fleet-event") || name.startsWith("fleet-mission")) {
    return true;
  }
  if (name === "fleet-calendar") return true;

  const path = route.path || "";
  return /\/fleets\/[^/]+\/(events|missions|calendar)/.test(path);
});

const comlink = useComlink();

onMounted(() => {
  comlink.on("fleet-update", refetch);
});
</script>

<template>
  <div>
    <NavItem
      :to="{ name: 'home' }"
      :label="t('nav.back')"
      icon="fa-light fa-chevron-left"
    />
    <template v-if="currentFleet">
      <NavItem
        :to="{ name: 'fleet', params: { slug: currentFleet.slug } }"
        :label="currentFleet.name"
        :image="currentFleet.logo?.smallUrl || undefined"
        :active="route.name === 'fleet'"
        prefix="00"
      />
      <NavItem
        v-if="currentFleet.publicFleet || membership"
        :to="{ name: 'fleet-ships', params: { slug: currentFleet.slug } }"
        :label="t('nav.fleets.ships')"
        :active="shipsNavActive"
        prefix="01"
        icon="fa-duotone fa-starship"
      />
      <NavItem
        v-if="currentFleet.publicFleetStats || membership"
        :to="{ name: 'fleet-stats', params: { slug: currentFleet.slug } }"
        :label="t('nav.fleets.stats')"
        :active="route.name === 'fleet-stats'"
        icon="fa-duotone fa-chart-bar"
        prefix="02"
      />
      <template v-if="membership">
        <NavItem
          :to="{ name: 'fleet-members', params: { slug: currentFleet.slug } }"
          :label="t('nav.fleets.members.index')"
          :active="String(route.name).startsWith('fleet-members')"
          icon="fa-duotone fa-users"
          prefix="03"
        />
        <NavItem
          v-if="
            hasLogisticsAccess &&
            isFeatureEnabled(FeatureFlagName.FLEET_LOGISTICS)
          "
          :to="{
            name: 'fleet-logistics',
            params: { slug: currentFleet.slug },
          }"
          :label="t('nav.fleets.logistics.index')"
          :active="String(route.name).startsWith('fleet-logistics')"
          icon="fa-duotone fa-boxes-stacked"
          prefix="04"
        />
        <NavItem
          v-if="
            (hasEventsAccess || hasMissionsAccess) &&
            isFleetFeatureEnabled(
              currentFleet,
              FeatureFlagName.FLEET_MISSION_BUILDER,
            )
          "
          :to="{
            name: 'fleet-events',
            params: { slug: currentFleet.slug },
          }"
          :label="t('nav.fleets.events.index')"
          :active="eventsNavActive"
          icon="fa-duotone fa-calendar-day"
          prefix="05"
        />
        <NavItem
          :to="{ name: 'fleet-settings', params: { slug: currentFleet.slug } }"
          :label="t('nav.fleets.settings.index')"
          :active="String(route.name).startsWith('fleet-settings')"
          icon="fa-duotone fa-cogs"
          prefix="06"
        />
      </template>
    </template>
  </div>
</template>
